"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import toast from "react-hot-toast";
import { useUser } from "@clerk/nextjs";
import {
  getLinkedStudyTask,
  completeLinkedStudyTask,
  getStudyTaskContinuation,
  completeStudyPageSchedule,
  setNextStudyGuideEnabled,
} from "@/lib/server-actions/studyPlanActions";
import {
  readStudyTaskLink,
  isSameStudyTaskLink,
  type StudyTaskLink,
  notifyStudyScheduleChanged,
  STUDY_PAGE_COMPLETED,
} from "@/lib/study-task-link";
import StudyScheduleFeedback from "./StudyScheduleFeedback";
import type { StudyPageScheduleResult } from "@/lib/study-page-schedule";
import type { ScheduledTask } from "@/lib/study-plan-state";
import type { StudyTaskContinuation } from "@/lib/study-task-navigation";
import { STUDY_PLAN_LABELS } from "@/lib/study-plan";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { CODE_LANG_BADGE } from "@/app/my/schedule/schedule-shared";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./ui/dialog";

/** 직접 방문도 출석·학습 완료 후 구독 중인 일정으로 이어갈 수 있다. */
export default function StudyTaskCompletion({ slug }: { slug: string }) {
  const params = useSearchParams();
  const link = useMemo(() => readStudyTaskLink(params), [params]);
  const { isSignedIn, user } = useUser();
  const identity = `${user?.id ?? ""}:${slug}:${link?.examSessionId}:${link?.planId}:${link?.taskKey}`;
  return (
    <StudyCompletionSession
      key={identity}
      slug={slug}
      link={link}
      signedIn={!!isSignedIn}
    />
  );
}

function StudyCompletionSession({
  slug,
  link,
  signedIn,
}: {
  slug: string;
  link: StudyTaskLink | null;
  signedIn: boolean;
}) {
  const [continuation, setContinuation] =
    useState<StudyTaskContinuation | null>(null);
  const [open, setOpen] = useState(false);
  const requestId = useRef(0);
  const alive = useRef(true);
  const opener = useRef<HTMLElement | null>(null);
  const navigating = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const showNext = useCallback(
    async (completedLink: StudyTaskLink | null = null) => {
      if (!signedIn || !alive.current) return;
      const request = ++requestId.current;
      opener.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      try {
        const result = await getStudyTaskContinuation(slug, completedLink);
        if (!alive.current || request !== requestId.current) return;
        if (!result.success) {
          toast.error(result.error);
          return;
        }
        if (result.continuation) {
          navigating.current = false;
          setContinuation(result.continuation);
          setOpen(true);
        }
      } catch {
        if (alive.current && request === requestId.current)
          toast.error(
            "학습은 저장했지만 다음 일정을 불러오지 못했어요. 학습 일정에서 확인해주세요.",
          );
      }
    },
    [slug, signedIn],
  );

  return (
    <>
      {link ? (
        <LinkedTask
          slug={slug}
          link={link}
          signedIn={signedIn}
          onCompleted={showNext}
        />
      ) : (
        <DirectStudyCompletion
          slug={slug}
          signedIn={signedIn}
          onCompleted={showNext}
        />
      )}
      {continuation && (
        <NextStudySheet
          continuation={continuation}
          open={open}
          onOpenChange={setOpen}
          onNavigate={() => {
            navigating.current = true;
            setOpen(false);
          }}
          shouldRestoreScroll={() => !navigating.current}
          onCloseAutoFocus={() => {
            if (!navigating.current && opener.current?.isConnected)
              opener.current.focus({ preventScroll: true });
          }}
        />
      )}
    </>
  );
}

function NextStudySheet({
  continuation,
  open,
  onOpenChange,
  onNavigate,
  shouldRestoreScroll,
  onCloseAutoFocus,
}: {
  continuation: StudyTaskContinuation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: () => void;
  shouldRestoreScroll: () => boolean;
  onCloseAutoFocus: () => void;
}) {
  const title = useRef<HTMLHeadingElement>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const saved = useRef(false);
  // 시트를 닫거나 링크로 떠나는 순간 체크돼 있으면 저장한다(JCG-3677). 떠난 뒤에도 요청은 이어지고
  // 토스트는 레이아웃 Toaster 에 뜬다. 다시 켜는 곳(/my/schedule 스위치)을 함께 안내한다.
  const saveDontShowAgain = () => {
    if (!dontShowAgain || saved.current) return;
    saved.current = true;
    void setNextStudyGuideEnabled(false)
      .then((result) => {
        if (result.success)
          toast.success(
            "다음 일정 안내를 더 이상 띄우지 않을게요. 학습 일정에서 다시 켤 수 있어요.",
            { duration: 5000 },
          );
        else {
          saved.current = false;
          toast.error(result.error ?? "설정을 저장하지 못했어요.");
        }
      })
      .catch(() => {
        saved.current = false;
        toast.error("설정을 저장하지 못했어요.");
      });
  };
  useBodyScrollLock(open, { shouldRestoreScroll });
  useEffect(() => {
    if (!open) return;
    document.documentElement.setAttribute("data-mobile-bottom-sheet-open", "");
    document.body.setAttribute("data-mobile-bottom-sheet-open", "");
    return () => {
      document.documentElement.removeAttribute("data-mobile-bottom-sheet-open");
      document.body.removeAttribute("data-mobile-bottom-sheet-open");
    };
  }, [open]);
  const next = continuation.next;
  const language = next
    ? { c: "C", java: "Java", python: "Python" }[next.codeLang]
    : "";
  const date = next
    ? new Intl.DateTimeFormat("ko-KR", {
        month: "long",
        day: "numeric",
        weekday: "short",
        timeZone: "Asia/Seoul",
      }).format(new Date(`${next.dateISO}T00:00:00+09:00`))
    : "";
  const handleNavigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey &&
      event.button === 0
    ) {
      saveDontShowAgain();
      onNavigate();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) saveDontShowAgain();
        onOpenChange(next);
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="not-prose top-auto bottom-0 max-h-[80dvh] max-w-lg translate-y-0 gap-5 overflow-y-auto overscroll-contain rounded-t-2xl rounded-b-none px-5 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 motion-reduce:animate-none"
        overlayClassName="motion-reduce:animate-none"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          title.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onCloseAutoFocus();
        }}
      >
        <div
          aria-hidden="true"
          className="mx-auto h-1 w-10 rounded-full bg-muted-foreground/30"
        />
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-2">
            <DialogTitle
              ref={title}
              tabIndex={-1}
              className="leading-normal outline-none"
            >
              {next ? "다음 일정도 이어서 공부할까요?" : "학습을 마쳤어요!"}
            </DialogTitle>
            <DialogDescription>
              {STUDY_PLAN_LABELS[continuation.planId]} · 내 학습일정
            </DialogDescription>
          </div>
          <label
            htmlFor="next-study-dont-show-again"
            className="-mr-1 -mt-1 flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 px-1 text-xs text-muted-foreground"
          >
            <Checkbox
              id="next-study-dont-show-again"
              checked={dontShowAgain}
              onCheckedChange={(value) => setDontShowAgain(value === true)}
            />
            다시 보지 않기
          </label>
        </div>
        {next ? (
          <div className="flex min-w-0 flex-col gap-1 rounded-xl bg-muted p-4">
            <p className="m-0 text-sm text-muted-foreground">{date}</p>
            <p className="m-0 break-words text-lg font-semibold">
              {next.reviewRound ? (
                <span className={`${CODE_LANG_BADGE} mr-2 align-middle`}>
                  {next.reviewRound}회독
                </span>
              ) : null}
              {next.title}
              {language ? ` (${language})` : ""}
            </p>
          </div>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">
            다음으로 이어갈 일정이 없어요. 전체 일정에서 진행 상황을 확인할 수
            있어요.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {/* 2열: 왼쪽 전체 일정, 오른쪽(주 동작) 다음 일정. 다음 일정이 없으면 한 칸 전체. */}
          <div className={next ? "grid grid-cols-2 gap-2" : "flex flex-col"}>
            <Button
              asChild
              variant={next ? "outline" : "default"}
              className="h-auto min-h-11 whitespace-normal"
            >
              <Link href="/my/schedule" onClick={handleNavigate}>
                전체 일정에서 선택하기
              </Link>
            </Button>
            {next && (
              <Button asChild className="h-auto min-h-11 whitespace-normal">
                <Link href={next.href} onClick={handleNavigate}>
                  다음 일정 공부하기
                </Link>
              </Button>
            )}
          </div>
          <DialogClose asChild>
            <Button variant="ghost" className="min-h-11">
              여기까지 공부할게요
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkedTask({
  slug,
  link,
  signedIn,
  onCompleted,
}: {
  slug: string;
  link: NonNullable<ReturnType<typeof readStudyTaskLink>>;
  signedIn: boolean;
  onCompleted: (link: StudyTaskLink) => Promise<void>;
}) {
  const [task, setTask] = useState<ScheduledTask | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const alive = useRef(true);
  const studiedLanguage = useRef<string | null>(null);
  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    if (signedIn) {
      getLinkedStudyTask(link, slug)
        .then((result) => {
          if (cancelled) return;
          if (result.success) setTask(result.task);
          else setError(result.error);
        })
        .catch(() => {
          if (!cancelled)
            setError("일정을 불러오지 못했어요. 새로고침해주세요.");
        });
    }
    return () => {
      cancelled = true;
      alive.current = false;
    };
  }, [signedIn, link, slug]);

  const complete = useCallback(async () => {
    if (!task || task.skipped || pending.current) return;
    if (task.done) {
      void onCompleted(link);
      return;
    }
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await completeLinkedStudyTask(link, slug);
      if (!alive.current) return;
      if (result.success) {
        setTask((previous) =>
          previous ? { ...previous, done: true } : previous,
        );
        notifyStudyScheduleChanged();
        void onCompleted(link);
      } else setError(result.error);
    } catch {
      if (alive.current)
        setError("출석은 유지됩니다. 일정 완료 저장을 다시 시도해주세요.");
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }, [task, link, slug, onCompleted]);

  useEffect(() => {
    const studied = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          slug: string;
          codeLang: string;
          link: StudyTaskLink | null;
        }>
      ).detail;
      if (detail?.slug !== slug || !isSameStudyTaskLink(detail.link, link))
        return;
      studiedLanguage.current = detail.codeLang;
      if (task && (!task.codeLang || detail.codeLang === task.codeLang)) {
        studiedLanguage.current = null;
        void complete();
      }
    };
    if (
      task &&
      studiedLanguage.current !== null &&
      (!task.codeLang || studiedLanguage.current === task.codeLang)
    ) {
      studiedLanguage.current = null;
      void complete();
    }
    window.addEventListener(STUDY_PAGE_COMPLETED, studied);
    return () => window.removeEventListener(STUDY_PAGE_COMPLETED, studied);
  }, [slug, task, complete, link]);

  const language = task?.codeLang
    ? { c: "C", java: "Java", python: "Python" }[task.codeLang]
    : "";
  return (
    <aside
      aria-label="공부 중인 일정"
      className="not-prose my-4 rounded-lg border border-border bg-muted/50 p-3 text-sm text-foreground"
    >
      <p className="break-words font-semibold">
        {task
          ? `${task.effectiveDateISO} · ${task.title}${language ? ` (${language})` : ""}`
          : "일정에서 이어서 공부 중"}
      </p>
      <p className="mt-1 text-muted-foreground" role="status">
        {!signedIn
          ? "로그인하면 이 일정의 완료 상태를 확인할 수 있어요."
          : task?.done
            ? "이 일정은 완료했어요."
            : task?.skipped
              ? "건너뛴 일정이에요."
              : task
                ? "출석 또는 ‘한 번 더 공부했어요’를 누르면 이 일정도 완료돼요."
                : error
                  ? "일정을 확인하지 못했어요."
                  : "일정을 확인하고 있어요…"}
      </p>
      {error && (
        <p role="alert" className="mt-2 text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {task &&
          !task.done &&
          !task.skipped &&
          (!task.attendanceBacked || error) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void complete()}
              className="min-h-11 rounded-md bg-foreground px-3 text-background hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-50"
            >
              {busy ? "저장 중…" : "이 일정 완료"}
            </button>
          )}
        <Link
          href="/my/schedule"
          className="inline-flex min-h-11 items-center rounded underline underline-offset-4 hover:text-amber-700 dark:hover:text-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          일정으로 돌아가기
        </Link>
      </div>
    </aside>
  );
}

function DirectStudyCompletion({
  slug,
  signedIn,
  onCompleted,
}: {
  slug: string;
  signedIn: boolean;
  onCompleted: (link?: StudyTaskLink | null) => Promise<void>;
}) {
  const [feedback, setFeedback] = useState<{
    result: StudyPageScheduleResult;
    sequence: number;
  } | null>(null);
  useEffect(() => {
    if (!signedIn) return;
    const studied = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          slug: string;
          link: StudyTaskLink | null;
          schedule?: StudyPageScheduleResult;
        }>
      ).detail;
      if (detail?.slug !== slug || detail.link || !detail.schedule) return;
      if (detail.schedule.kind === "completed") {
        toast.success(`${detail.schedule.task.title} 일정도 완료했어요.`, {
          duration: 4000,
        });
        void onCompleted(detail.schedule.task);
      } else if (detail.schedule.kind === "none") {
        void onCompleted();
      }
      setFeedback((previous) => ({
        result: detail.schedule!,
        sequence: (previous?.sequence ?? 0) + 1,
      }));
    };
    window.addEventListener(STUDY_PAGE_COMPLETED, studied);
    return () => window.removeEventListener(STUDY_PAGE_COMPLETED, studied);
  }, [slug, signedIn, onCompleted]);
  if (!feedback) return null;
  return (
    <StudyScheduleFeedback
      key={feedback.sequence}
      initialResult={feedback.result}
      onComplete={async (choice, automatic) => {
        const result = await completeStudyPageSchedule(choice, slug, automatic);
        if (result.success) {
          notifyStudyScheduleChanged();
          void onCompleted(choice);
        }
        return result;
      }}
    />
  );
}
